<template>
  <v-container>
    <v-row id="controls">
      <v-col cols="12">
        <h4 class="overline mb-1 d-flex">NIST SP 800-53 (Rev. 5) Controls</h4>
        <v-card id="controlTable" flat color="bg_darkerish">
          <v-card-title class="font-weight-light pt-0 pb-2">
            <v-row no-gutters>
              <v-col cols="12" order="1" sm="4" order-sm="1" class="px-2">
                <v-select
                  v-model="playFilters"
                  :items="playOptions"
                  @change="searchAndFilter"
                  :disabled="noMappedPlays"
                  multiple
                  label="Filter by Play"
                  color="dw_green"
                  item-color="dw_green"
                  item-text="id"
                  return-object
                  single-line
                  hide-details
                  clearable
                  :clear-icon="mdiClose"
                  class="text-caption"
                ></v-select>
              </v-col>
              <v-col cols="12" order="3" sm="4" order-sm="2" class="px-2">
                <v-select
                  v-model="familyFilter"
                  :items="familyOptions"
                  @change="searchAndFilter"
                  label="Filter by Family"
                  color="dw_green"
                  item-color="dw_green"
                  item-text="name"
                  return-object
                  single-line
                  hide-details
                  clearable
                  :clear-icon="mdiClose"
                  class="text-caption"
                >
                  <template v-slot:item="{ attrs, on, item }">
                    <v-list-item
                      ripple
                      v-bind="attrs"
                      v-on="on">
                      <v-list-item-content>
                        <v-list-item-title class="body-2 font-weight-light">
                          {{ item.id.toUpperCase() }} - {{ item.name }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span>{{ item.id.toUpperCase() }} - {{ item.name }}</span>
                  </template>
                </v-select>
              </v-col>
              <v-col cols="12" order="4" sm="4" order-sm="3" class="px-2">
                <v-text-field
                  v-model="search"
                  @keydown.enter="searchAndFilter"
                  label="Search"
                  color="dw_green"
                  clearable
                  @click:clear="() => search === null ? searchAndFilter : null"
                  :clear-icon="mdiClose"
                  single-line
                  hide-details
                  class="text-caption">
                  <template v-slot:append>
                    <v-icon small class="ml-1" v-text="mdiLayersSearchOutline" />
                  </template>
                </v-text-field>
              </v-col>
              <v-col cols="12" order="2" sm="4" order-sm="4" class="px-2">
                <v-switch
                  v-model="noMappedPlays"
                  label="No Mapped Plays"
                  @change="searchAndFilter"
                  color="dw_green"
                  dense
                  hide-details
                ></v-switch>
              </v-col>
            </v-row>
          </v-card-title>

          <v-data-table
            :headers="headers"
            :items="controls"
            :items-per-page.sync="itemsPerPage"
            :page.sync="pageNum"
            :single-select="true"
            @click:row="(rowItem, { select }) => select(rowItem)"
            @item-selected="({ item }) =>
              $router.push({ name: 'control_view', params: { id: item.id }})"
            item-key="sort_id"
            show-select
            class="elevation-1 bg_darkerish"
            color="dw_green"
            hide-default-footer
          >
            <template v-slot:no-data>
              <div class="font-weight-light text-end">No Controls found</div>
            </template>
            <template v-slot:item.data-table-select="{ isSelected, select }">
              <div @click="select">
                <v-icon v-if="isSelected" small color="dw_green" v-text="mdiRecordCircleOutline" />
                <v-icon v-else small color="white" v-text="mdiCircleOutline" />
              </div>
            </template>
            <template v-slot:item.name="{ value }">
              <div class="font-weight-light">{{ value }}</div>
            </template>
            <template v-slot:item.title="{ value }">
              <div class="text-capitalize font-weight-light">{{ value.toLowerCase() }}</div>
            </template>
            <template v-slot:item.family="{ value }">
              <div class="font-weight-light">{{ displayFamily(value) }}</div>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon
                v-text="mdiShareVariantOutline"
                small
                color="dw_green"
                @click.stop="shareControl(item.id)"
                :title="`Share ${item.label} via PDF`" />
            </template>

            <template v-slot:footer>
              <v-divider></v-divider>
              <v-row class="caption pa-2" no-gutters align="center" justify="center">
                <v-col cols="4" class="d-flex ml-2">
                  <v-row no-gutters align-content="start">
                    <v-col  cols="6" align-self="center">
                      <span class="grey--text">Rows per page:</span>
                    </v-col>
                    <v-col cols="4">
                      <v-select
                        dense
                        v-model="itemsPerPage"
                        :items="perPage"
                        item-text="text"
                        item-value="value"
                        color="dw_green"
                        item-color="dw_green"
                        single-line
                        hide-details
                        title="Rows per Page"
                        class="ml-2 pb-1 pl-1 my-auto caption"
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-col>
                <v-spacer></v-spacer>
                <span class="mx-4 grey--text">
                  {{ pageStart + 1 }}-{{ pageStop }} of {{ controlCount }}
                </span>
                <v-btn
                  fab
                  x-small
                  text
                  class="mr-1"
                  title="Previous Page"
                  :disabled="prevPageDisabled"
                  @click="goToPrevPage">
                  <v-icon small v-text="mdiChevronLeft" />
                </v-btn>
                <v-btn
                  fab
                  x-small
                  text
                  class="mr-1"
                  title="Next Page"
                  :disabled="nextPageDisabled"
                  @click="goToNextPage">
                  <v-icon small v-text="mdiChevronRight" />
                </v-btn>
              </v-row>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>
    <router-view
      @filter-by-fam="filterByFam"
      :filters="{ fam: familyFilter }"
      @share-control="shareControl"
    ></router-view>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import {
  mdiLayersSearchOutline,
  mdiCircleOutline,
  mdiRecordCircleOutline,
  mdiShareVariantOutline,
  mdiClose,
  mdiCheckboxBlankOutline,
  mdiCheckboxMarked,
  mdiChevronLeft,
  mdiChevronRight
} from '@mdi/js';
import apiService from '@/services/api.service';

export default {
  name: 'controlPanel',
  data() {
    return {
      mdiLayersSearchOutline,
      mdiCircleOutline,
      mdiRecordCircleOutline,
      mdiShareVariantOutline,
      mdiClose,
      mdiCheckboxBlankOutline,
      mdiCheckboxMarked,
      mdiChevronLeft,
      mdiChevronRight,
      search: '',
      playFilters: [],
      noMappedPlays: false,
      familyFilter: undefined,
      headers: [
        {
          text: 'Control',
          value: 'label',
          align: 'start',
          width: '10vw',
          sortable: false,
          class: 'font-weight-regular'
        },
        {
          text: 'Name',
          value: 'title',
          width: '30vw',
          sortable: false,
          class: 'font-weight-regular'
        },
        {
          text: 'Family',
          value: 'family',
          sortable: false,
          class: 'font-weight-regular'
        },
        {
          text: 'Actions',
          value: 'actions',
          align: 'end',
          width: '10vw',
          sortable: false,
          class: 'font-weight-regular'
        }
      ],
      itemsPerPage: 5,
      perPage: [
        { text: '5', value: 5 },
        { text: '25', value: 25 }
      ],
      pageNum: 1,
      numTablePages: 1,
      pageStart: 0,
      pageStop: 5,
      controlCount: 0,
      queryPrevPage: null,
      queryNextPage: null,
      queryPrevOffset: 0,
      queryCurrentOffset: 0,
      queryNextOffset: 25,
      prevPageDisabled: true,
      nextPageDisabled: false
    };
  },
  watch: {
    search() {
      if (this.search === null) {
        this.search = '';
        this.searchAndFilter();
      }
    },
    noMappedPlays() {
      this.playFilters = [];
    },
    controlPagination() {
      this.handlePagination();
    },
    itemsPerPage() {
      this.pageNum = 1;
      this.handlePagination();
    }
  },
  computed: {
    ...mapGetters([
      'controls',
      'controlPagination',
      'playOptions',
      'familyOptions'
    ])
  },
  mounted() {
    if (this.controlPagination.count) this.handlePagination();
  },
  methods: {
    ...mapActions([
      'fetchControls',
      'exportRMFToPDF'
    ]),
    ...mapMutations([
      'throwError'
    ]),
    filterByFam(type, fam) {
      if (type === 'add') {
        this.familyFilter = fam;
      } else {
        this.familyFilter = undefined;
      }
      this.searchAndFilter();
      this.$vuetify.goTo('#controlTable');
    },
    displayFamily(val) {
      const family = this.familyOptions.find((fam) => fam.id === val);
      return family.name;
    },
    async shareControl(id) {
      let control;
      try {
        control = await apiService.get(`controls/${id}/`);
      } catch {
        this.throwError('Unable to retrieve control information');
        return;
      }
      try {
        const html = await this.exportRMFToPDF(control);
        const pdfWindow = window.open('');
        pdfWindow.document.write(html);
        pdfWindow.print();
      } catch {
        this.throwError('Unable to share NIST RMF Control');
      }
    },
    async searchAndFilter() {
      let endpoint = '?ordering=sort_id&type=SP800-53';
      endpoint += this.search.length > 1 ? `&search=${this.search}` : '';
      if (this.noMappedPlays) endpoint += '&no_plays=true';
      this.playFilters.forEach((play) => { endpoint += `&plays__id=${play.id}`; });
      endpoint += this.familyFilter ? `&family__name=${this.familyFilter.name}` : '';
      await this.fetchControls(endpoint);
    },
    handlePagination() {
      this.controlCount = this.controlPagination.count;
      this.queryPrevPage = this.controlPagination.previous;
      this.queryNextPage = this.controlPagination.next;
      this.numTablePages = Math.ceil(this.controls.length / this.itemsPerPage);

      if (this.queryPrevPage !== null) {
        this.queryPrevPage = new URL(this.queryPrevPage);
        const offset = this.queryPrevPage.searchParams.get('offset');
        this.queryPrevOffset = offset === null ? 0 : parseInt(offset, 10);
      }
      if (this.queryNextPage !== null) {
        this.queryNextPage = new URL(this.queryNextPage);
        const offset = this.queryNextPage.searchParams.get('offset');
        this.queryNextOffset = offset === null ? 0 : parseInt(offset, 10);
      }

      if (this.queryPrevPage === null) {
        this.queryCurrentOffset = 0;
      } else if (this.queryNextPage === null) {
        this.queryCurrentOffset = this.queryNextOffset;
      } else {
        this.queryCurrentOffset = (this.queryPrevOffset + this.queryNextOffset) / 2;
      }

      this.pageStart = this.queryCurrentOffset;
      let nextPageStop = this.queryCurrentOffset + this.itemsPerPage;
      nextPageStop = nextPageStop > this.controlCount ? this.controlCount : nextPageStop;
      this.pageStop = nextPageStop;
      this.prevPageDisabled = this.pageStart === 0;
      this.nextPageDisabled = this.pageStop === this.controlCount;
    },
    async goToNextPage() {
      if (this.pageNum === (this.controls.length / this.itemsPerPage)) {
        await this.fetchControls(this.queryNextPage.search);
        let nextPageStop = this.queryCurrentOffset + this.itemsPerPage;
        nextPageStop = nextPageStop > this.controlCount ? this.controlCount : nextPageStop;
        this.pageNum = 1;
        this.pageStart = this.queryCurrentOffset;
        this.pageStop = nextPageStop;
      } else {
        this.pageNum += 1;
        this.pageStart += this.itemsPerPage;
        this.pageStop += this.itemsPerPage;
        if (this.pageStop > this.controlCount) this.pageStop = this.controlCount;
      }
      this.setDisabled();
    },
    async goToPrevPage() {
      if (this.pageNum === 1 && this.queryNextOffset > 0) {
        await this.fetchControls(this.queryPrevPage.search);
        this.pageNum = this.numTablePages;
        this.pageStart = this.queryNextOffset - this.itemsPerPage;
        this.pageStop = this.queryNextOffset;
      } else {
        this.pageNum -= 1;
        this.pageStart -= this.itemsPerPage;
        if (this.pageStop === this.controlCount) {
          this.pageStop = this.pageStart + this.itemsPerPage;
        } else this.pageStop -= this.itemsPerPage;
      }
      this.setDisabled();
    },
    setDisabled() {
      if (this.queryNextPage === null) {
        if (this.pageStop === this.controlCount) this.nextPageDisabled = true;
        else this.nextPageDisabled = false;
      } else this.nextPageDisabled = false;
      if (this.queryPrevPage === null) {
        if (this.pageStart === 0) this.prevPageDisabled = true;
        else this.prevPageDisabled = false;
      } else this.prevPageDisabled = false;
    }
  }
};
</script>
